/**
 * Customer-portal purchase-order line items — EditableDataGrid consumer (W4).
 *
 *   useLocalEditableGridStore ──▶ handle.Provider ──▶ EditableDataGrid
 *        │  owns rows (no useState)          │
 *        │                                   ├─ onCreate  ▶ CreateCustomerPortalLineItem
 *        └── handleRequest (per-row queue) ──┼─ onEdit    ▶ UpdateCustomerPortalLineItem
 *                                            └─ onDelete  ▶ DeleteCustomerPortalLineItem
 *
 * READ-ONLY IS ONE PROP. `canEdit` is the core's switch (ruling B4,
 * EditableDataGrid.tsx:32-47); this surface must never re-implement it, because
 * an external customer sees this grid and only a DRAFT order may be touched:
 *
 *   purchaseOrder.status === 'DRAFT'  ──▶ canEdit
 *      ├─ false ▶ no cell editable · no single-click entry
 *      ├─ false ▶ no "Add new product" button, no Tab-to-new-row
 *      └─ false ▶ `actions` column hidden ⇒ no delete affordance
 *
 * DELETE IS HAND-WIRED, not a grid prop — the core never calls `onDelete`.
 * `remove()` marks the row deleting and only hard-deletes on resolve; a
 * REJECTED delete restores the row, where the legacy grid dropped it from the
 * UI forever.
 */
import { Stack } from '@mui/material';
import { GridRowId, useGridApiRef } from '@mui/x-data-grid-premium';
import { useCallback, useMemo } from 'react';
import Money from 'shared/utils/Money';
import AutoCompleteCell from 'src/components/atoms/grid/AutoCompleteCell';
import BoldHeader from 'src/components/atoms/grid/BoldHeader';
import UnderlinedMatch from 'src/components/atoms/UnderlinedMatch';
import { AgText } from 'src/components/ui';
import type { RequestQueue } from 'src/hooks/useQueuedRequests';
import useSystemUnitRounding from 'src/hooks/useSystemUnitRounding';
import type { GridRow } from 'src/lib/gridRows.store';
import { EditableDataGrid, GridColObj, useLocalEditableGridStore } from 'src/modules/grids';
import columnActions from 'src/utils/grid/column/columnActions';
import type { CustomerPortalLineItemInput } from 'types/graphql';
import { z } from 'zod';
import useCustomerPortalApi from '../useCustomerPortalApi';
import { CustomerPortalProduct } from '../useCustomerPortalProducts';
import { CustomerPortalPurchaseOrderData } from './useCustomerPortalPurchaseOrder';

type Props = { purchaseOrder: CustomerPortalPurchaseOrderData; products: CustomerPortalProduct[] };

/**
 * Widened from {@link CustomerPortalProduct} on `code` alone: the zod model
 * below parses it as `string | null` (a product with no code arrives as null),
 * while the query type declares it optional. Both shapes flow into this row.
 */
type PortalLineItemProduct = { id: number; code?: string | null; label: string; price: number; unit?: string };

export type PortalLineItemRow = {
  id?: number;
  productId?: number;
  unitsOrdered?: number;
  /** Read-only projections carried by the query; never edited on this surface. */
  unitsShipped?: number;
  sellPrice?: number;
  product?: PortalLineItemProduct | null;
};

/** The SDL requires BOTH fields (`Int!`) on create AND update — alias the codegen type. */
export type PortalLineItemInput = CustomerPortalLineItemInput;

export const PORTAL_LINE_ITEM_MODEL = z.object({
  product: z
    .object(
      { id: z.number(), code: z.string().nullable(), label: z.string(), price: z.number() },
      { invalid_type_error: 'Must select a product' }
    )
    .refine((value) => !!value.id, 'Must select a product'),
  unitsOrdered: z.number({ invalid_type_error: 'Must order at least 1 unit' }).min(1, 'Must order at least 1 unit'),
});

/** Row-level gating is not used here — the whole grid is gated by `canEdit`. */
const PORTAL_CAPABILITIES = { canAddEntity: () => true, canEditEntity: () => true, canDeleteEntity: () => true };

export const createFreshRow = (source?: Partial<PortalLineItemRow>): PortalLineItemRow => ({
  ...source,
  product: null,
  unitsOrdered: 0,
});

/** Merges CHANGES over the ROW and sends the FULL input — the SDL requires both fields on every mutation. The validation model guarantees both before any save reaches the queue; the throw is a belt-and-braces guard landing in the queue's row-error path. */
export const rowToInput = (
  changes: Partial<GridRow<PortalLineItemRow>>,
  row: GridRow<PortalLineItemRow>
): PortalLineItemInput => {
  const merged = { ...row, ...changes };
  const productId = merged.product?.id ?? merged.productId;
  const unitsOrdered = merged.unitsOrdered;

  if (productId === undefined || unitsOrdered === undefined) {
    throw new Error('Portal line item needs a product and units before it can be saved');
  }

  return { productId, unitsOrdered };
};

export default function LineItemsGrid({ purchaseOrder, products }: Props) {
  const apiRef = useGridApiRef();
  const unitRounding = useSystemUnitRounding();
  const { createLineItem, updateLineItem, deleteLineItem } = useCustomerPortalApi();

  const canEdit = purchaseOrder?.status === 'DRAFT';

  /** Read ONCE at construction — the bridge re-reads `source` only when `entityId` changes. Safe because the parent gates this component behind loaded products and none of the three line-item mutations refetches the order, so `lineItems` never churns. Loosening that gate would freeze this join. */
  const source = useMemo(
    () => ({
      entityId: purchaseOrder?.id ?? 'no-order',
      rows: (purchaseOrder?.lineItems ?? []).map(
        (lineItem): PortalLineItemRow => ({
          ...lineItem,
          product: products.find((product) => product.id === lineItem.productId) ?? null,
        })
      ),
    }),
    [purchaseOrder?.id, purchaseOrder?.lineItems, products]
  );

  const initialState = useMemo(
    () => ({ capabilities: PORTAL_CAPABILITIES, validationModel: PORTAL_LINE_ITEM_MODEL }),
    []
  );

  const { Provider, handleRequest, remove } = useLocalEditableGridStore<PortalLineItemRow, PortalLineItemInput>({
    initialState,
    source,
  });

  /**
   * The bridge hands back a queue that accepts create · update · DELETE; the
   * core grid's prop is typed for create · update only. `RequestQueue`'s event
   * parameter is measured COVARIANT (it sits behind a distributive conditional),
   * so the wider queue is not directly assignable even though it accepts a
   * superset of requests. Forwarding through a narrowed callback is the honest
   * bridge — no cast, no widening of the core's contract.
   */
  const gridRequest = useCallback<RequestQueue<PortalLineItemRow, PortalLineItemInput, 'create' | 'update'>>(
    (rowId, request) => handleRequest(rowId, request),
    [handleRequest]
  );

  const onCreate = useCallback(
    async ({ input }: { input: PortalLineItemInput }): Promise<PortalLineItemRow> => {
      const created = await createLineItem(purchaseOrder?.id, input);
      return { id: created?.id };
    },
    [createLineItem, purchaseOrder?.id]
  );

  /** RETURNS the promise: dropping it made a failed update an unhandled rejection with the row still showing the doomed value. The queue awaits it and parks a row error. */
  const onEdit = useCallback(
    async ({ id, input }: { id: number; input: PortalLineItemInput }): Promise<PortalLineItemRow> => {
      await updateLineItem(id, input);
      return { id };
    },
    [updateLineItem]
  );

  const onDelete = useCallback(
    async (lineItemId: number): Promise<PortalLineItemRow> => {
      await deleteLineItem(lineItemId);
      return { id: lineItemId };
    },
    [deleteLineItem]
  );

  const handleDeleteLineItem = useCallback(
    (rowId: GridRowId) => {
      const removal = remove(rowId);

      if (!removal) {
        return;
      }

      handleRequest(removal.rowId, {
        event: onDelete,
        eventType: 'delete',
        unpackPayload: removal.payload,
        resolve: removal.resolve,
        reject: removal.reject,
      });
    },
    [remove, handleRequest, onDelete]
  );

  const columns: GridColObj<GridRow<PortalLineItemRow>> = useMemo(
    () => ({
      product: {
        headerName: 'Products',
        flex: 1,
        editable: true,
        renderHeader: BoldHeader,
        renderEditCell: (params) => (
          <AutoCompleteCell<CustomerPortalProduct>
            {...params}
            options={products}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={(option) => option.label}
            filterOptions={(options, state) => {
              if (!state.inputValue) return options;
              return options.filter((option) => {
                const labelMatch = option.label.toLowerCase().includes(state.inputValue.toLowerCase());
                const codeMatch = option.code?.toLowerCase().includes(state.inputValue.toLowerCase());
                return labelMatch || codeMatch;
              });
            }}
            renderOption={(props, option, { inputValue }) => (
              <Stack>
                <div>
                  <UnderlinedMatch target={option.label} input={inputValue} />
                </div>
                <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
                  <AgText size="14" color="secondary">
                    {Money.toHuman(option.price)}
                  </AgText>
                  {option.code && (
                    <AgText size="14" color="secondary">
                      <UnderlinedMatch target={option.code} input={inputValue} />
                    </AgText>
                  )}
                </Stack>
              </Stack>
            )}
          />
        ),
        // Null-safe: a draft row carries `product: null`, and the legacy form
        // (`b?.label.localeCompare(a?.label)`) returned `undefined` for it.
        sortComparator: (a: PortalLineItemProduct | null, b: PortalLineItemProduct | null) =>
          (b?.label ?? '').localeCompare(a?.label ?? ''),
        valueFormatter: (value: PortalLineItemProduct | null) => value?.label,
      },
      unitsOrdered: {
        headerName: 'Units Ordered',
        type: 'number',
        minWidth: 120,
        flex: 1,
        editable: true,
        valueSetter: (value, row) => ({ ...row, unitsOrdered: unitRounding(value) }),
        renderHeader: BoldHeader,
      },
      price: {
        headerName: 'Price Per Unit',
        type: 'number',
        minWidth: 120,
        flex: 1,
        renderHeader: BoldHeader,
        valueGetter: (_, row) => (row.product ? Money.toHuman(row.product.price) : ''),
      },
      total: {
        headerName: 'Total Price',
        type: 'number',
        minWidth: 120,
        flex: 1,
        renderHeader: BoldHeader,
        valueGetter: (_, row) => {
          if (!row.product || !row.unitsOrdered) {
            return '';
          }
          return Money.toHuman(row.product.price * row.unitsOrdered);
        },
      },
      actions: {
        type: 'actions',
        width: 10,
        maxWidth: 10,
        align: 'left',
        getActions: columnActions(({ id }) => ({ deleteRow: () => handleDeleteLineItem(id) }), { showOnHover: true }),
      },
    }),
    [unitRounding, products, handleDeleteLineItem]
  );

  if (!purchaseOrder) return null;

  return (
    <Provider>
      <EditableDataGrid<PortalLineItemRow, PortalLineItemInput>
        gridKey="customer-portal.purchase-order.line-items"
        storageKey="customer-portal-purchase-order-line-items"
        getRowHeight={() => 'auto'}
        apiRef={apiRef}
        columns={columns}
        pinnedColumns={{ right: ['actions'] }}
        gridId="products"
        newRowLabel="Add new product"
        canEdit={canEdit}
        createFreshRow={createFreshRow}
        handleRequest={gridRequest}
        onCreate={onCreate}
        onEdit={onEdit}
        validationModel={PORTAL_LINE_ITEM_MODEL}
        rowToInput={rowToInput}
      />
    </Provider>
  );
}
